import { useEffect, useState } from 'react';
import { adminApi, type AdminLiveEvent } from '../../lib/api';
import { SortableList } from './SortableList';

const STATUS_OPTIONS = [
  { value: 'on_sale', label: 'On sale' },
  { value: 'free', label: 'Free entry' },
  { value: 'last_tickets', label: 'Ultimi posti' },
  { value: 'sold_out', label: 'Sold out' },
];

const empty: Partial<AdminLiveEvent> = {
  date: '', date_label: '', city: '', venue: '', ticket_url: '',
  status: 'on_sale', notes: '', is_active: true,
};

export default function LiveAdminPanel() {
  const [items, setItems] = useState<AdminLiveEvent[]>([]);
  const [editing, setEditing] = useState<Partial<AdminLiveEvent> | null>(null);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    setItems(await adminApi.liveList());
    setLoading(false);
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    if (!editing) return;
    const data: any = { ...editing };
    if (data.date === '') data.date = null;
    if (editing.id) await adminApi.liveUpdate(editing.id, data);
    else await adminApi.liveCreate(data);
    setEditing(null);
    reload();
  }
  async function remove(id: number) {
    if (!confirm('Eliminare questa data?')) return;
    await adminApi.liveDelete(id);
    reload();
  }
  async function reorder(next: AdminLiveEvent[]) {
    setItems(next);
    await adminApi.liveReorder(next.map((i) => i.id));
  }

  return (
    <section className="admin-panel">
      <div className="admin-panel-head">
        <h2>Date Live</h2>
        <button className="btn-primary" onClick={() => setEditing({ ...empty })}>+ Nuova data</button>
      </div>

      {loading && <p className="muted">Caricamento...</p>}
      {!loading && items.length === 0 && <p className="muted">Nessuna data.</p>}

      <SortableList
        items={items}
        onReorder={reorder}
        renderItem={(item, handle) => (
          <div className="admin-row">
            {handle}
            <div className="admin-row-main">
              <div className="admin-row-title">
                <strong>{item.venue}</strong> · {item.city}
                {!item.is_active && <span className="badge-muted">nascosto</span>}
              </div>
              <div className="muted">
                {item.date ? new Date(item.date).toLocaleString('it-IT') : (item.date_label || '—')} · {item.status}
              </div>
            </div>
            <div className="admin-row-actions">
              <button className="btn-ghost" onClick={() => setEditing({ ...item })}>Modifica</button>
              <button className="btn-danger" onClick={() => remove(item.id)}>Elimina</button>
            </div>
          </div>
        )}
      />

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Modifica data' : 'Nuova data'}>
          <Field label="Città"><input value={editing.city || ''} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></Field>
          <Field label="Locale"><input value={editing.venue || ''} onChange={(e) => setEditing({ ...editing, venue: e.target.value })} /></Field>
          <Field label="Data e ora">
            <input
              type="datetime-local"
              value={editing.date ? toLocalDT(editing.date) : ''}
              onChange={(e) => setEditing({ ...editing, date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
            />
          </Field>
          <Field label="Etichetta data (se senza data)"><input value={editing.date_label || ''} onChange={(e) => setEditing({ ...editing, date_label: e.target.value })} placeholder="Es. Coming soon" /></Field>
          <Field label="Link biglietti"><input value={editing.ticket_url || ''} onChange={(e) => setEditing({ ...editing, ticket_url: e.target.value })} /></Field>
          <Field label="Stato">
            <select value={editing.status || 'on_sale'} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Note"><input value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
          <Field label="Visibile"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /></Field>
          <div className="modal-actions">
            <button className="btn-ghost" onClick={() => setEditing(null)}>Annulla</button>
            <button className="btn-primary" onClick={save}>Salva</button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function toLocalDT(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="admin-field"><span>{label}</span>{children}</label>;
}

export function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="admin-modal-bg" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-head">
          <h3>{title}</h3>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal-body">{children}</div>
      </div>
    </div>
  );
}
